import { ClockIcon, LoaderCircleIcon, ServerIcon } from "lucide-react";

import type { PolicyJob, PolicyProcessingState } from "../../../state/policies";
import {
  formatDuration,
  formatTimestamp,
  jobDurationSeconds,
  timeAgo,
  totalDurationSeconds,
} from "./documentTime";

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  upload: "Upload",
  onedrive: "OneDrive",
  sharepoint: "SharePoint",
  googledrive: "Google Drive",
};

function StatusIndicator({ status }: { status: string | null }) {
  if (status === "completed") {
    return (
      <div className="flex items-center gap-2">
        <div className="size-2 rounded-full bg-success" />
        <span className="text-sm font-medium">Completed</span>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex items-center gap-2">
        <div className="size-2 rounded-full bg-destructive" />
        <span className="text-sm font-medium text-destructive">Failed</span>
      </div>
    );
  }
  if (status === null) {
    return (
      <div className="flex items-center gap-2">
        <div className="size-2 rounded-full bg-muted-foreground/40" />
        <span className="text-sm font-medium text-muted-foreground">Not processed</span>
      </div>
    );
  }
  if (status === "queued") {
    return (
      <div className="flex items-center gap-2">
        <div className="size-2 rounded-full bg-muted-foreground/40" />
        <span className="text-sm font-medium text-muted-foreground">Queued</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <LoaderCircleIcon className="size-3.5 animate-spin text-primary" />
      <span className="text-sm font-medium text-primary">Processing</span>
    </div>
  );
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-5 py-4">
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * The four facts a page about a processing run opens with: when the document
 * arrived, where its ingestion stands, how long it took, and where it came
 * from. Fixed four-column grid — every document answers all four, so the row
 * never reflows around missing metadata.
 */
export function DocumentDetailsGrid({
  createdAt,
  sourceType,
  processing,
  jobs,
  now,
}: {
  createdAt: string;
  sourceType: string;
  processing: PolicyProcessingState | null;
  jobs: ReadonlyArray<PolicyJob>;
  now: number;
}) {
  const current = jobDurationSeconds(processing, now);
  const total = totalDurationSeconds(jobs, now);

  return (
    <div className="grid grid-cols-2 divide-y divide-border/60 rounded-xl border border-border/60 bg-card sm:grid-cols-4 sm:divide-x sm:divide-y-0">
      <Cell label="Uploaded">
        <div className="flex items-center gap-2" title={formatTimestamp(createdAt)}>
          <ClockIcon className="size-3.5 text-muted-foreground/60" />
          <span className="text-sm font-medium">{timeAgo(createdAt, now)}</span>
        </div>
      </Cell>

      <Cell label="Status">
        <StatusIndicator status={processing?.status ?? null} />
      </Cell>

      <Cell label="Duration">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <ClockIcon className="size-3.5 text-muted-foreground/60" />
            <span className="font-mono text-sm font-medium tabular-nums">
              {formatDuration(current)}
            </span>
            {jobs.length > 1 ? (
              <span className="text-[10px] text-muted-foreground">this run</span>
            ) : null}
          </div>
          {/* Only meaningful once a document has been through more than one
              run — otherwise it would repeat the line above. */}
          {jobs.length > 1 ? (
            <div className="flex items-center gap-2">
              <span className="w-3.5" />
              <span className="font-mono text-xs tabular-nums text-muted-foreground">
                {formatDuration(total)}
              </span>
              <span className="text-[10px] text-muted-foreground">
                across {jobs.length} runs
              </span>
            </div>
          ) : null}
        </div>
      </Cell>

      <Cell label="Source">
        <div className="flex items-center gap-2">
          <ServerIcon className="size-3.5 text-muted-foreground/60" />
          <span className="text-sm font-medium">{SOURCE_LABELS[sourceType] ?? sourceType}</span>
        </div>
      </Cell>
    </div>
  );
}
